import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";
import { pinKey, pin, unpin, getState, setPinAuto } from "@/lib/offline";

const AUTO_KEY = "rh.autoPins";
const DEFAULTS = { favorites: false, playlists: [], wifiOnly: true };
const read = () => { try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(AUTO_KEY) || "null") || {}) }; } catch { return { ...DEFAULTS }; } };
let auto = read();
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
export const subscribeAuto = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const getAuto = () => auto;
export const isAutoPlaylist = (id) => auto.playlists.includes(id);

const write = (next) => { auto = next; localStorage.setItem(AUTO_KEY, JSON.stringify(next)); emit(); };
export const setWifiOnly = (on) => { write({ ...auto, wifiOnly: on }); scheduleSync(0); };
export const setAutoFavorites = (on) => { write({ ...auto, favorites: on }); if (on) scheduleSync(0); else unpin("favorites", "favorites").catch(() => {}); };
// Turning auto off keeps the playlist as a manual pin unless `remove` is set.
export const setAutoPlaylist = (id, on, remove = false) => {
  const playlists = on ? [...new Set([...auto.playlists, id])] : auto.playlists.filter((x) => x !== id);
  write({ ...auto, playlists });
  if (on) { setPinAuto("playlist", id, true).catch(() => {}); scheduleSync(0); }
  else if (remove) unpin("playlist", id).catch(() => {});
  else setPinAuto("playlist", id, false).catch(() => {});
};

// Network Information API: only Chromium on Android reports type/saveData reliably; elsewhere we assume Wi-Fi.
const conn = () => navigator.connection || navigator.mozConnection || navigator.webkitConnection;
export const canDetectNetwork = () => { const c = conn(); return !!c && (c.type !== undefined || c.saveData !== undefined); };
export const onMobileData = () => { const c = conn(); return !!c && (c.type === "cellular" || c.saveData === true); };
if (conn()?.addEventListener) conn().addEventListener("change", () => scheduleSync(1500));

let status = { syncing: false, last: localStorage.getItem("rh.autoPins.last") || null, error: null, waitingForWifi: false };
const setStatus = (patch) => { status = { ...status, ...patch }; if (patch.last) localStorage.setItem("rh.autoPins.last", patch.last); emit(); };
export const getAutoStatus = () => status;

// Replace a pin's track list in place: download new songs, drop ones no longer present.
async function syncPin({ type, id, name, subtitle, tracks, coverAlbumIds, userId }) {
  const existing = getState().pins.find((p) => p.key === pinKey(type, id));
  if (!existing) { if (tracks.length) await pin({ type, id, name, subtitle, tracks, coverAlbumIds, userId, auto: true }); return; }
  const wanted = new Set(tracks.map((t) => t.id));
  const have = new Set(existing.tracks.map((t) => t.id));
  const same = wanted.size === have.size && [...wanted].every((x) => have.has(x));
  if (same && existing.name === name) { if (!existing.auto) await setPinAuto(type, id, true); return; }
  await pin({ type, id, name, subtitle, tracks, coverAlbumIds, userId, auto: true });
}

let rerun = false;
export async function syncAuto(userId, force = false) {
  if (!navigator.onLine) return;
  if (status.syncing) { rerun = true; return; }
  if (!auto.favorites && !auto.playlists.length) return;
  if (auto.wifiOnly && onMobileData() && !force) { setStatus({ waitingForWifi: true }); return; }
  setStatus({ syncing: true, error: null, waitingForWifi: false });
  try {
    if (auto.favorites) {
      const { data } = await api.get("/tracks", { params: { starred: true, limit: 1000 } });
      if (auto.favorites) await syncPin({ type: "favorites", id: "favorites", name: "Favorites", subtitle: "Starred songs", tracks: data.items, coverAlbumIds: [], userId });
    }
    for (const pid of [...auto.playlists]) {
      try {
        const { data } = await api.get(`/playlists/${pid}`);
        if (!auto.playlists.includes(pid)) continue;
        await syncPin({ type: "playlist", id: pid, name: data.name, subtitle: data.description || "", tracks: data.tracks, coverAlbumIds: data.cover_album_ids || [], userId });
      } catch (e) {
        if (e.response?.status === 404) { write({ ...auto, playlists: auto.playlists.filter((x) => x !== pid) }); await unpin("playlist", pid).catch(() => {}); } else throw e;
      }
    }
    setStatus({ last: new Date().toISOString() });
  } catch (e) {
    setStatus({ error: e.message || "Sync failed" });
  } finally {
    setStatus({ syncing: false });
    if (rerun) { rerun = false; scheduleSync(500); }
  }
}

let timer = null;
let currentUser = null;
export const setAutoUser = (uid) => { currentUser = uid; };
export const scheduleSync = (delay = 4000) => {
  clearTimeout(timer);
  timer = setTimeout(() => syncAuto(currentUser), delay);
};

const TOUCH = [/^\/tracks\/[^/]+\/star$/, /^\/albums\/[^/]+\/star$/, /^\/playlists(\/|$)/, /^\/library\/remove$/, /^\/dupes\/resolve$/, /^\/tracks\/[^/]+$/];
api.interceptors.response.use((r) => {
  const m = (r.config.method || "get").toLowerCase();
  if (m !== "get" && TOUCH.some((rx) => rx.test((r.config.url || "").split("?")[0]))) scheduleSync();
  return r;
});
window.addEventListener("online", () => scheduleSync(2000));
setInterval(() => scheduleSync(0), 15 * 60 * 1000);

let snap = { auto, status };
const snapshot = () => { if (snap.auto !== auto || snap.status !== status) snap = { auto, status }; return snap; };
export const useAutoPins = () => useSyncExternalStore(subscribeAuto, snapshot);
